import { api } from "@/lib/api";

/**
 * Sites and rosters — what the hours hang off.
 *
 * A site carries its default shift (O-17), which is what keeps the entry
 * screen at three taps: the manager confirms 07:00-15:00 rather than typing it
 * eighty-five times.
 */

export interface Gradiliste {
  id: string;
  naziv: string;
  adresa: string | null;
  country_code: string | null;
  distance_from_office_km: string | null;
  aktivan: boolean;
  voditelj_korisnik_id: string | null;
  voditelj: string | null;
  zamjenik_korisnik_id: string | null;
  zamjenik: string | null;
  smjena_pocetak: string;
  smjena_kraj: string;
  broj_prijave_gradilista: string | null;
  voditelj_telefon: string | null;
  poslovoda_ime: string | null;
  poslovoda_telefon: string | null;
  strucnjak_znr_ime: string | null;
  strucnjak_znr_telefon: string | null;
}

export interface GradilisteUnos {
  naziv: string;
  adresa?: string | null;
  country_code?: string | null;
  distance_from_office_km?: string | null;
  voditelj_korisnik_id?: string | null;
  zamjenik_korisnik_id?: string | null;
  smjena_pocetak: string;
  smjena_kraj: string;
  aktivan: boolean;
  broj_prijave_gradilista?: string | null;
  voditelj_telefon?: string | null;
  poslovoda_ime?: string | null;
  poslovoda_telefon?: string | null;
  strucnjak_znr_ime?: string | null;
  strucnjak_znr_telefon?: string | null;
}

export interface Raspored {
  brigada_id?: string | null;
  takt_id?: string | null;
  id: string;
  radnik_id: string;
  radnik: string | null;
  gradiliste_id: string;
  gradiliste: string | null;
  vrijedi_od: string;
  vrijedi_do: string | null;
  override_active?: boolean;
  ceka_blokada_override?: boolean;
  blocking_reasons?: string[];
  upozorenje?: string | null;
}

export interface RasporedZaOdobrenje extends Raspored {
  requested_at: string;
}

export interface IshodBatchRasporeda {
  radnik_id: string;
  status: "uspjeh" | "ceka_override" | "odbijen";
  raspored: Raspored | null;
  razlog: string | null;
}

export async function dohvatiGradilista(
  samoAktivna = true,
  signal?: AbortSignal,
): Promise<Gradiliste[]> {
  const response = await api.get<Gradiliste[]>("/gradilista", {
    params: { samo_aktivna: samoAktivna },
    signal,
  });
  return response.data;
}

export async function spremiGradiliste(
  podaci: GradilisteUnos,
): Promise<Gradiliste> {
  const response = await api.post<Gradiliste>("/gradilista", podaci);
  return response.data;
}

export async function izmijeniGradiliste(
  id: string,
  podaci: GradilisteUnos,
): Promise<Gradiliste> {
  const response = await api.patch<Gradiliste>(`/gradilista/${id}`, podaci);
  return response.data;
}

export async function dohvatiRaspored(
  gradilisteId?: string,
  naDan?: string,
  signal?: AbortSignal,
  radnikId?: string,
): Promise<Raspored[]> {
  const response = await api.get<Raspored[]>("/raspored", {
    params: { gradiliste_id: gradilisteId, na_dan: naDan, radnik_id: radnikId },
    signal,
  });
  return response.data;
}

export async function dohvatiRasporedeZaOdobrenje(
  signal?: AbortSignal,
): Promise<RasporedZaOdobrenje[]> {
  const response = await api.get<RasporedZaOdobrenje[]>(
    "/raspored/za-odobrenje",
    { signal },
  );
  return response.data;
}

export async function rasporediRadnika(podaci: {
  zavrsi_raspored_id?: string | null;
  brigada_id?: string | null;
  radnik_id: string;
  gradiliste_id: string;
  vrijedi_od: string;
  vrijedi_do?: string | null;
}): Promise<Raspored> {
  const response = await api.post<Raspored>("/raspored", podaci);
  return response.data;
}

export async function rasporediRadnikeBatch(podaci: {
  brigada_id?: string | null;
  premjesti?: boolean;
  radnik_ids: string[];
  gradiliste_id: string;
  vrijedi_od: string;
}): Promise<IshodBatchRasporeda[]> {
  const response = await api.post<IshodBatchRasporeda[]>("/raspored/batch", podaci);
  return response.data;
}

export async function odobriBlokaduOverride(
  rasporedId: string,
  reason: string,
): Promise<Raspored> {
  const response = await api.post<Raspored>(
    `/raspored/${rasporedId}/blokada-override`,
    { reason },
  );
  return response.data;
}

export async function zavrsiRaspored(
  id: string,
  podaci: {
    radnik_id: string;
    gradiliste_id: string;
    vrijedi_od: string;
    vrijedi_do: string | null;
  },
): Promise<Raspored> {
  const response = await api.patch<Raspored>(`/raspored/${id}`, podaci);
  return response.data;
}
